import { useState, useEffect, useMemo, useCallback } from 'react';
import { useGetAssignableRolesQuery, useGetUserModuleRolesQuery, useGetUserEffectivePermissionsQuery } from '../../../../../api/endpoints/permissionManagement';
import { useGetCountryByKunnrQuery } from '../../../../../api/endpoints/country';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { validateTCNumber } from '../../../../../utils/validationUtils';
import { USER_TYPES } from '../../constants';

const EMPTY_ACCOUNT = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    identityNo: '',
    isEmailEnabled: true,
    dealer: null,
    addrId: null,
    country: '',
    countryName: '',
    password: '',
    passwordConfirmed: ''
};

const accountFromRow = (user) => ({
    ...EMPTY_ACCOUNT,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    phoneNumber: user.phoneNumber || '',
    identityNo: user.identityNo ? String(user.identityNo).padStart(11, '0') : '',
    isEmailEnabled: user.isEmailEnabled !== false,
    dealer: user.dealerId ? { id: user.dealerId, name: user.dealerName, text: user.dealerName, displayValue: user.dealerName } : null,
    addrId: user.addrId || null,
    country: user.country || '',
    countryName: user.countryName || ''
});

/**
 * Yeni kullanıcı / rol düzenleme formu durumu.
 * Modül kutusu rollerden türetilir; kullanıcı modül seçer, modül kapanınca o modülün rolleri temizlenir.
 */
export const useUserAccessForm = ({ visible, user }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const isEditMode = !!user;
    const userId = user?.userId || null;

    const [account, setAccount] = useState(EMPTY_ACCOUNT);
    const [userType, setUserType] = useState(USER_TYPES.DEALER);
    const [selectedModules, setSelectedModules] = useState([]);
    const [selectedRoleIds, setSelectedRoleIds] = useState([]);
    const [errors, setErrors] = useState({});
    const [initialized, setInitialized] = useState(false);

    const { data: assignable = [], isFetching: assignableLoading } = useGetAssignableRolesQuery(userType, { skip: !visible });
    const { data: userRoles, isFetching: userRolesLoading } = useGetUserModuleRolesQuery(userId, { skip: !visible || !userId, refetchOnMountOrArgChange: true });
    const { data: effective, isFetching: effectiveLoading } = useGetUserEffectivePermissionsQuery(userId, { skip: !visible || !userId, refetchOnMountOrArgChange: true });

    const lang = Number(getCurrentLanguageId?.());
    const dealerId = account.dealer?.id || null;
    const { data: countryRes, isFetching: countryLoading } = useGetCountryByKunnrQuery({ dealerId, languageId: lang === 1 || lang === 2 ? lang : 1 }, { skip: !visible || isEditMode || !dealerId });

    // Dialog açılışında formu sıfırla
    useEffect(() => {
        if (!visible) {
            setInitialized(false);
            return;
        }
        setAccount(user ? accountFromRow(user) : EMPTY_ACCOUNT);
        setUserType(USER_TYPES.DEALER);
        setSelectedModules([]);
        setSelectedRoleIds([]);
        setErrors({});
        setInitialized(!user);
    }, [visible, user]);

    // Düzenlemede mevcut rolleri yükle
    useEffect(() => {
        if (!visible || !userRoles || initialized) return;
        setUserType(userRoles.userType || USER_TYPES.DEALER);
        setSelectedModules(userRoles.moduleCodes || []);
        setSelectedRoleIds((userRoles.moduleRoles || []).map((r) => r.roleId));
        setInitialized(true);
    }, [visible, userRoles, initialized]);

    useEffect(() => {
        if (!countryRes || isEditMode) return;
        setAccount((prev) => ({ ...prev, country: countryRes.countryCode || '', countryName: countryRes.countryName || '' }));
    }, [countryRes, isEditMode]);

    // Kullanıcı tipi değişince atanamayan rolleri düşür
    useEffect(() => {
        if (assignableLoading || !initialized) return;
        const allowed = new Set(assignable.flatMap((m) => m.roles.map((r) => r.roleId)));
        setSelectedRoleIds((prev) => prev.filter((id) => allowed.has(id)));
        setSelectedModules((prev) => prev.filter((code) => assignable.some((m) => m.moduleCode === code)));
    }, [assignable, assignableLoading, initialized]);

    const changeAccount = useCallback((field, value) => {
        setAccount((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    }, []);

    const changeDealer = useCallback((dealer) => {
        setAccount((prev) => ({ ...prev, dealer, addrId: null, country: '', countryName: '' }));
        setErrors((prev) => ({ ...prev, dealer: undefined }));
    }, []);

    // DealerAddressDropdown bayi değişince onChange(null) çağırır; düzenlemede hesap bilgisi değişmez
    const changeAddress = useCallback(
        (address) => {
            if (isEditMode) return;
            setAccount((prev) => ({ ...prev, addrId: address?.id || null }));
            setErrors((prev) => ({ ...prev, addrId: undefined }));
        },
        [isEditMode]
    );

    const moduleRoleIds = useCallback((moduleCode) => (assignable.find((m) => m.moduleCode === moduleCode)?.roles || []).map((r) => r.roleId), [assignable]);

    const toggleModule = useCallback(
        (moduleCode) => {
            setErrors((prev) => ({ ...prev, roles: undefined }));
            if (selectedModules.includes(moduleCode)) {
                const ids = new Set(moduleRoleIds(moduleCode));
                setSelectedModules((prev) => prev.filter((c) => c !== moduleCode));
                setSelectedRoleIds((prev) => prev.filter((id) => !ids.has(id)));
                return;
            }
            setSelectedModules((prev) => [...prev, moduleCode]);
        },
        [selectedModules, moduleRoleIds]
    );

    const allModulesSelected = assignable.length > 0 && assignable.every((m) => selectedModules.includes(m.moduleCode));

    const toggleAllModules = useCallback(() => {
        setErrors((prev) => ({ ...prev, roles: undefined }));
        if (allModulesSelected) {
            setSelectedModules([]);
            setSelectedRoleIds([]);
            return;
        }
        setSelectedModules(assignable.map((m) => m.moduleCode));
    }, [allModulesSelected, assignable]);

    const toggleRole = useCallback((roleId) => {
        setErrors((prev) => ({ ...prev, roles: undefined }));
        setSelectedRoleIds((prev) => (prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]));
    }, []);

    const toggleAllRolesOfModule = useCallback(
        (moduleCode, checked) => {
            const ids = moduleRoleIds(moduleCode);
            setSelectedRoleIds((prev) => (checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))));
        },
        [moduleRoleIds]
    );

    // Modül başına etkin rol = en yüksek öncelikli seçili rol (Identity ile aynı kural)
    const effectiveRoleByModule = useMemo(() => {
        const result = {};
        assignable.forEach((m) => {
            const chosen = m.roles.filter((r) => selectedRoleIds.includes(r.roleId)).sort((a, b) => b.priority - a.priority);
            if (chosen.length > 0) result[m.moduleCode] = chosen[0];
        });
        return result;
    }, [assignable, selectedRoleIds]);

    const validate = () => {
        const next = {};
        if (!isEditMode) {
            const required = th('authorizationV2.validation.required');
            if (!account.firstName.trim()) next.firstName = required;
            if (!account.lastName.trim()) next.lastName = required;
            if (!account.email.trim()) next.email = required;
            else if (!/\S+@\S+\.\S+/.test(account.email)) next.email = th('authorizationV2.validation.email');
            const digits = (account.phoneNumber || '').replace(/\D/g, '');
            if (!digits) next.phoneNumber = required;
            else if (digits.length < 7 || digits.length > 15) next.phoneNumber = th('authorizationV2.validation.phone');
            if (!account.identityNo) next.identityNo = required;
            else if (!validateTCNumber(account.identityNo)) next.identityNo = th('authorizationV2.validation.identityNo');
            if (!account.dealer?.id) next.dealer = required;
            if (!account.addrId) next.addrId = required;
            if (!account.country) next.country = countryLoading ? th('authorizationV2.validation.countryLoading') : required;
            if (!account.password) next.password = required;
            else if (account.password.length < 8) next.password = th('authorizationV2.validation.passwordLength');
            if (account.password !== account.passwordConfirmed) next.passwordConfirmed = th('authorizationV2.validation.passwordMismatch');
        }
        const emptyModule = selectedModules.find((code) => !moduleRoleIds(code).some((id) => selectedRoleIds.includes(id)));
        if (emptyModule) next.roles = th('authorizationV2.validation.moduleWithoutRole');
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const buildPayload = () => ({
        account: { ...account, dealerId: account.dealer?.id || 0 },
        userType,
        moduleCodes: selectedModules,
        roleIds: selectedRoleIds
    });

    return {
        isEditMode,
        userId,
        account,
        changeAccount,
        changeDealer,
        changeAddress,
        countryLoading,
        userType,
        setUserType,
        assignable,
        assignableLoading,
        rolesLoading: userRolesLoading || (isEditMode && !initialized),
        otherRoles: userRoles?.otherRoles || [],
        selectedModules,
        toggleModule,
        allModulesSelected,
        toggleAllModules,
        selectedRoleIds,
        toggleRole,
        toggleAllRolesOfModule,
        effectiveRoleByModule,
        effective,
        effectiveLoading,
        errors,
        validate,
        buildPayload
    };
};
